import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import DOMPurify from 'dompurify';
import {
    FaCodeBranch,
    FaChevronDown,
    FaChevronUp,
    FaExternalLinkAlt
} from 'react-icons/fa';

/**
 * Checks whether a string contains HTML markup (as opposed to plain text/Markdown).
 * Used to decide whether to sanitise-and-render as HTML or pass through ReactMarkdown.
 */
const containsHtml = (text) => {
    if (!text) return false;
    return /<(div|p|br|table|tr|td|th|span|a|img|h[1-6]|ul|ol|li|pre|blockquote|hr|html|head|body)\b/i.test(text);
};

/**
 * Renders the body of a merged comment.
 * If the content contains HTML (e.g. an email reply that was stored as a comment)
 * it is sanitised with DOMPurify before rendering.  Plain text / Markdown is
 * rendered with ReactMarkdown.
 */
const renderMergedCommentBody = (commentText) => {
    if (!commentText) return null;

    if (containsHtml(commentText)) {
        const sanitised = DOMPurify.sanitize(commentText, {
            FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form'],
            ALLOW_DATA_ATTR: false
        });
        return (
            <div
                className='comment-body comment-body-markdown'
                dangerouslySetInnerHTML={{ __html: sanitised }}
            />
        );
    }

    return (
        <div className='comment-body comment-body-markdown'>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {commentText}
            </ReactMarkdown>
        </div>
    );
};

/**
 * MergedCommentsSection
 *
 * Renders all comments that were imported from merged action items, grouped by
 * their source item and displayed in collapsible sections.  Each section shows:
 *   • A header with the source item ID (clickable link), merge date, and count
 *   • All transferred comments in chronological order (oldest first)
 *
 * Showing merged comments in their own dedicated area (rather than mixed
 * chronologically with native comments) makes it immediately clear which
 * discussion originated elsewhere, and the link lets users navigate to the
 * now-closed source item for full context.
 *
 * @param {Object[]} mergedCommentGroups
 *   Each element: { sourceItemId, comments, mergeDate, mergedByUserId }
 *   comments are already sorted oldest-first by the parent's useMemo.
 * @param {Function} getUserName   (userId: number) => string
 * @param {Function} formatCommentDateTime  (dateValue: any) => string
 */
const MergedCommentsSection = ({ mergedCommentGroups, getUserName, formatCommentDateTime }) => {
    const navigate = useNavigate();

    /**
     * Track collapsed/expanded state keyed by source item ID.
     * Default: all groups are expanded so merged comments are immediately visible.
     */
    const [collapsedGroups, setCollapsedGroups] = useState({});

    /**
     * Toggle the collapsed state for a given source item group.
     * @param {string} sourceItemId
     */
    const toggleGroup = (sourceItemId) => {
        setCollapsedGroups(prev => ({
            ...prev,
            [sourceItemId]: !prev[sourceItemId]
        }));
    };

    // Nothing to render if there are no merged comment groups
    if (!mergedCommentGroups || mergedCommentGroups.length === 0) {
        return null;
    }

    return (
        <div className='merged-conversations-section'>

            {/* ── Section title ──────────────────────────────────────────────── */}
            <div className='merged-conversations-heading'>
                <FaCodeBranch size={13} />
                <span>Imported conversations from merged items</span>
            </div>

            {/* ── One collapsible group per source action item ──────────────── */}
            {mergedCommentGroups.map(group => {
                const isCollapsed = collapsedGroups[group.sourceItemId] ?? false;
                const commentCount = group.comments.length;

                return (
                    <div
                        key={group.sourceItemId}
                        className='merged-conversation-group'
                    >
                        {/* Group toggle header */}
                        <button
                            type='button'
                            className='merged-conversation-group-header'
                            onClick={() => toggleGroup(group.sourceItemId)}
                            aria-expanded={!isCollapsed}
                            title={`${isCollapsed ? 'Show' : 'Hide'} comments from Action Item #${group.sourceItemId}`}
                        >
                            {/* Expand / collapse chevron */}
                            <span className='merged-group-chevron'>
                                {isCollapsed
                                    ? <FaChevronDown size={11} />
                                    : <FaChevronUp size={11} />
                                }
                            </span>

                            {/* Branch icon */}
                            <FaCodeBranch size={12} className='merged-group-branch-icon' />

                            {/* Source item link */}
                            <span className='merged-group-title'>
                                From Action Item{' '}
                                {/* Button styled as a link — stops click propagation so
                                    navigating to the source doesn't toggle the group */}
                                <button
                                    type='button'
                                    className='merged-group-item-link'
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(`/action-items/${group.sourceItemId}`);
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            navigate(`/action-items/${group.sourceItemId}`);
                                        }
                                    }}
                                    title={`View Action Item #${group.sourceItemId} (closed)`}
                                >
                                    #{group.sourceItemId}
                                    <FaExternalLinkAlt size={9} className='merged-group-item-link-icon' />
                                </button>
                            </span>

                            {/* Merge date (if known) */}
                            {group.mergeDate && (
                                <span className='merged-group-date'>
                                    merged {formatCommentDateTime(group.mergeDate)}
                                </span>
                            )}

                            {/* Comment count pill */}
                            <span className='merged-group-count'>
                                {commentCount} comment{commentCount !== 1 ? 's' : ''}
                            </span>
                        </button>

                        {/* Expanded comment list */}
                        {!isCollapsed && (
                            <div className='merged-conversation-comments'>
                                {group.comments.map(comment => (
                                    <div
                                        key={comment.logId}
                                        className='action-item-log-comment-row merged-comment'
                                    >
                                        <div className='comment-header'>
                                            <span className='comment-username'>
                                                {getUserName(comment.changedByUserId)}
                                            </span>
                                            <span className='comment-date'>
                                                {formatCommentDateTime(comment.changedAt)}
                                            </span>
                                        </div>
                                        {renderMergedCommentBody(comment.comment)}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default MergedCommentsSection;
